import { MouseEvent, ReactNode, useCallback, useMemo } from 'react';

import { GridColDef, GridRenderCellParams, GridRowHeightParams } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';

import DashboardDataGrid from 'src/common/components/dataGrid/DashboardDataGrid';
import { handymanActionsColumn } from 'src/common/components/dataGrid/dashboardGridColumns';
import { DASHBOARD_GRID_ROW_HEIGHT } from 'src/common/components/dataGrid/useDashboardGridHeight';
import { LanguageKeys } from 'src/common/constants/languages';
import dayjs from 'src/helpers/dayjs';
import { IEdition } from 'src/interfaces/Edition';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

import { EditionPrimaryRowBand, EditionRowActionsMenu, EditionYearCell } from './editionTableCells';

interface Props {
  activeEditionId?: string;
  editions: IEdition[];
  isLoading?: { [key: string]: boolean };
  onDeleteEdition: (editionId: string, e?: MouseEvent<unknown>) => void;
  onEditEdition: (editionId: string, e?: MouseEvent<unknown>) => void;
  onSaveEdition: (editedEdition: IEdition, id?: string) => void;
  onSaveStatistics: (editionId: string, e?: MouseEvent<unknown>) => void;
  onToggleActiveEdition: (editionId: string, e?: MouseEvent<unknown>) => void;
}

const EditionsTable = ({
  activeEditionId,
  editions,
  isLoading,
  onDeleteEdition,
  onEditEdition,
  onSaveEdition,
  onSaveStatistics,
  onToggleActiveEdition,
}: Props) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const { dateFormat, dateTimeFormat } = useSystemOptionsStore(systemOptionsState => systemOptionsState);

  const getRowHeight = useCallback(
    (params: GridRowHeightParams) => {
      const row = params.model as IEdition;
      if (isLoading?.[row.id]) {
        return 160;
      }
      return DASHBOARD_GRID_ROW_HEIGHT;
    },
    [isLoading],
  );

  const wrapPrimaryBand = useCallback(
    (content: ReactNode) => <EditionPrimaryRowBand>{content}</EditionPrimaryRowBand>,
    [],
  );

  const renderYearCell = useCallback(
    ({ row }: GridRenderCellParams<IEdition>) => (
      <EditionYearCell edition={row} isLoading={!!isLoading?.[row.id]} />
    ),
    [isLoading],
  );

  const columns = useMemo((): GridColDef<IEdition>[] => {
    return [
      {
        field: 'year',
        headerName: t('editions.eventYear'),
        flex: 1,
        minWidth: 200,
        sortable: false,
        renderCell: renderYearCell,
      },
      {
        field: 'date',
        headerName: t('editions.eventDate'),
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: ({ row }) =>
          wrapPrimaryBand(dayjs(row.date).format(dateFormat[currentLang])),
      },
      {
        field: 'signupDeadline',
        headerName: t('editions.signupDeadline'),
        width: 180,
        sortable: false,
        renderCell: ({ row }) =>
          wrapPrimaryBand(dayjs(row.signupDeadline).format(dateTimeFormat[currentLang])),
      },
      handymanActionsColumn<IEdition>(t, 'editions.optionsLabel', ({ row: edition }) =>
        wrapPrimaryBand(
          <EditionRowActionsMenu
            edition={edition}
            activeEditionId={activeEditionId}
            onDeleteEdition={onDeleteEdition}
            onEditEdition={onEditEdition}
            onSaveEdition={onSaveEdition}
            onSaveStatistics={onSaveStatistics}
            onToggleActiveEdition={onToggleActiveEdition}
          />,
        ),
      ),
    ];
  }, [
    activeEditionId,
    currentLang,
    dateFormat,
    dateTimeFormat,
    onDeleteEdition,
    onEditEdition,
    onSaveEdition,
    onSaveStatistics,
    onToggleActiveEdition,
    renderYearCell,
    t,
    wrapPrimaryBand,
  ]);

  return (
    <DashboardDataGrid<IEdition>
      aria-label={t('editions.editionsTableLabel')}
      chrome="compact"
      layout="autoHeight"
      rows={editions}
      columns={columns}
      getRowHeight={getRowHeight}
      sx={theme => ({
        '& .MuiDataGrid-row': {
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: theme.palette.primary.main,
        },
      })}
    />
  );
};

export default EditionsTable;
